import * as React from 'react';
import { inject, observer } from 'mobx-react';
import * as styles from "./ComponentEditPanel.css";
import { AppAdminStore } from '../stores';
import { LocalizationService } from '../../../services/LocalizationService';
import ContentOrganizerService from '../../../services/ContentOrganizerService';
import { IInstance } from '../../../interfaces/IInstance';
import InstanceService from '../../../services/InstanceService';
import { ITreeViewItem } from '../../../interfaces/ITreeViewItem';
import { IAGroup } from '../../group/Group';
import { IAOptionSelector } from '../../optionSelector/OptionSelector';
import { IATreeview } from '../../treeview/Treeview';
import { IATextField } from '../../textField/TextField';
import { IADropDown } from '../../dropDown/DropDown';
import { IAToggle } from '../../toggle/Toggle';
import { IAIconPicker } from '../../iconPicker/IconPicker';
import { IANumberField } from '../../numberField/NumberField';
import { IAColorPicker } from '../../colorPicker/ColorPicker';
import { IATermCheckbox } from '../../termCheckBox';
import { IAPanel } from '../../panels';
import { IOptionSelectorItem } from '../../../interfaces/IOptionSelectorIcon';
import { IAppComponent } from '../../../interfaces/IAppComponent';
import { IContentOrganizerCreate } from '../../../interfaces/IContentOrganizerCreate';
import { IASkeleton } from '../../skeleton/Skeleton';

export interface IProps {
  appAdminStore?: AppAdminStore;
  open: boolean;
  hasChanged?: () => void;
}

export interface IState {
  instancesForComponent: string;
  instances?: IInstance[];
  searchText?: string;
  isLoadingSharepointSitesQuery?: boolean;
  isLoadingContentOrganizers?: boolean;
  sharePointSites?: any[];
  contentOrganizerInstances?: any[];
  displayTooManyDaysErrorMessage?: boolean;
}

@inject('appAdminStore')
@observer
export class ComponentEditPanel extends React.Component<IProps, IState> {

  private localizationService: LocalizationService = new LocalizationService();
  private searchDelay;
  private queryDelay: NodeJS.Timeout;
  private readonly min = 1;
  private readonly max = 364;

  constructor(props: IProps) {
    super(props);
    this.state = {
      instancesForComponent: undefined
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.open) {
      let component = this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex];
      if (component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events" || component.type === "IntraActive-ContentOrganizer" || component.type === "IntraActive-NavigatorAdmin" || component.type === "IntraActive-Events" || component.type === "IntraActive-ContentOrganizerPortal")) {
        this.loadInstances();
      }
      if (component && (component.type === "IntraActive-AIAssistant") && this.state.contentOrganizerInstances == undefined) {
        this.loadContentOrganizerInstances();
      }
    }
  }

  private loadInstances(): Promise<void> {
    return new Promise(async (resolve) => {
      let component = this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex].components[this.props.appAdminStore.selectedComponentIndex];
      if (component && this.state.instancesForComponent !== component.type) {
        this.setState({ instancesForComponent: component.type });
        if (component.type === "IntraActive-ContentOrganizerPortal") {
          this.props.appAdminStore.loadingInstances = true;
          const instances = await ContentOrganizerService.getContentOrganizerInstancesList(this.props.appAdminStore.environment, this.props.appAdminStore.token, this.props.appAdminStore.tenant, "false");
          if (instances) {
            this.props.appAdminStore.loadingInstances = false;
            const tempArray = [];
            instances.forEach((instance: IInstance) => {
              tempArray.push({
                checked: false,
                children: undefined,
                id: instance.siteCollection,
                title: instance.title
              });
            });
            if (component.contentOrganizers && component.contentOrganizers.length > 0) {
              tempArray.forEach((contentOrganizer: any) => {
                component.contentOrganizers.forEach((selectedContentOrganizer: string) => {
                  if (contentOrganizer.id === selectedContentOrganizer) {
                    contentOrganizer.checked = true;
                  }
                })
              });
            }
            const contentOrganizer = [
              {
                checked: false,
                children: tempArray,
                id: this.localizationService.strings.AppAdmin_SelectAllContentOrganizers,
                title: this.localizationService.strings.AppAdmin_SelectAllContentOrganizers,
              }
            ]
            this.props.appAdminStore.availableInstancesForComponent = contentOrganizer;
            this.props.appAdminStore.checkForChanges();
            if (this.props.hasChanged) {
              this.props.hasChanged();
            }
          }
          this.render();
        } else {
          this.props.appAdminStore.availableInstancesForComponent = undefined;
          this.props.appAdminStore.loadingInstances = true;
          const instances = await InstanceService.getInstances(this.props.appAdminStore.environment, this.props.appAdminStore.token, this.props.appAdminStore.tenant, component.type, true)
          if (instances) {
            let component = this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex];
            instances.forEach((instance: IInstance, index) => {
              if (instance.id === component?.instance?.id) {
                component.instance = instance;
                this.props.appAdminStore.loadingInstances = false;
                this.render();
              }
              if (index === instances?.length - 1 && !component?.instance?.id) {
                component.instance = instances[0];
                this.props.appAdminStore.loadingInstances = false;
                this.render();
              }
            });
          }
          this.setState({ instances });
          const tempArray = [];
          tempArray.push({
            "disabled": true,
            "key": undefined,
            "value": this.localizationService.strings.AppAdmin_SelectAnInstance
          });
          instances.forEach((instance: IInstance) => {
            if (instance.instanceName == null) {
              instance.instanceName = "Default";
            }
            tempArray.push({
              "key": instance?.id,
              "value": instance.title
            });
          });
          this.props.appAdminStore.availableInstancesForComponent = tempArray;
          this.props.appAdminStore.checkForChanges();
          if (this.props.hasChanged) {
            this.props.hasChanged();
          }
        }
      }
      resolve();
    });
  }

  private childrenCategoriesAreChecked(category: any, id: string): void {
    if (category.children) {
      category.children.forEach((child: any) => {
        if (id === child.guid) {
          child.checked = true;
        }
        this.childrenCategoriesAreChecked(child, id);
      });
    }
  }

  private getComponentFromType(): string {
    let component;
    switch (this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex].type) {
      case "IntraActive-Messages":
        component = "Messages"
        break;
      case "IntraActive-Events":
        component = "Event"
        break;
      default:
        break;
    }
    return component;
  }

  private hasSelectedChild(item: ITreeViewItem): void {
    let component = this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex].components[this.props.appAdminStore.selectedComponentIndex];
    item.children?.forEach((child: ITreeViewItem) => {
      if (child.checked) {
        component.categories.push(
          {
            id: child.guid,
            name: child.name
          }
        );
      };
      this.hasSelectedChild(child);
    });
  }

  private loadContentOrganizerInstances(): void {
    this.setState({ isLoadingContentOrganizers: true }, () => {
      ContentOrganizerService.getContentOrganizerInstancesList(this.props.appAdminStore.environment, "000", this.props.appAdminStore.tenant, true)
        .then((contentOrganizerInstances: Partial<IContentOrganizerCreate>[]) => {
          const messages: Partial<IContentOrganizerCreate> = {
            siteCollection: "IntraActive-Messages",
            title: "Messages"
          };
          contentOrganizerInstances.unshift(messages);
          this.setState({ contentOrganizerInstances });
        })
        .finally(() => {
          this.setState({ isLoadingContentOrganizers: false });
        });
    });
  }

  private searchSharepointSites(): void {
    this.setState({ isLoadingSharepointSitesQuery: true }, () => {
      ContentOrganizerService.getSites(this.props.appAdminStore.tenant, "000", this.props.appAdminStore.environment, this.state.searchText, 100, 0)
        .then((result) => {
          if (result?.items?.length > 0) {
            const items: Partial<IContentOrganizerCreate>[] = result.items.map(item => {
              const siteCollection = item.path.split("/sites/")[1];
              return {
                siteCollection: siteCollection ?? "root",
                title: item.title
              }
            });
            this.setState({ sharePointSites: items });
          } else {
            this.setState({ sharePointSites: [] });
          }
          console.log(result);
        })
        .finally(() => {
          this.setState({ isLoadingSharepointSitesQuery: false });
        });
    });
  }

  private renderSites(contentOrganizerInstances: Partial<IContentOrganizerCreate>[]): JSX.Element[] {
    let component: IAppComponent = this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex];
    return contentOrganizerInstances?.map(contentOrganizer => {
      const selectedIndex: number = (component.aiSites && component.aiSites?.findIndex(co => co.siteCollection && co.siteCollection === contentOrganizer.siteCollection)) ?? -1;
      const isSelected = selectedIndex >= 0;
      return isSelected ?
        <></>
        :
        <div
          key={`site_${contentOrganizer.siteCollection}`}
          onClick={() => {
            if (!component.aiSites) {
              component.aiSites = [contentOrganizer];
            } else {
              component.aiSites.push(contentOrganizer);
            }
            this.props.appAdminStore.checkForChanges();
            if (this.props.hasChanged) {
              this.props.hasChanged();
            }
            this.forceUpdate();
            this.props.hasChanged();
          }}
          className={styles.IA_aiSite}
          style={{
            backgroundColor: "#eeeeee",
            color: "#4f4e4d"
          }}
        >
          <b>{contentOrganizer.title}</b><br />
          {contentOrganizer.siteCollection}
        </div>
    });
  }

  public render(): JSX.Element {
    let content;
    const componentSelectorOptions: IOptionSelectorItem[] = [
      {
        icon: 'Chat',
        key: 'IntraActive-Messages',
        text: this.localizationService.strings.AppAdmin_ComponentMessages,
      },
      {
        icon: 'Calendar',
        key: 'IntraActive-Events',
        text: this.localizationService.strings.AppAdmin_ComponentEvents,
      },
      {
        iconUrl: 'https://intraactivestorage.blob.core.windows.net/cdn/extra%20icons/ic_sharepoint.svg',
        key: 'Sharepoint-News',
        text: this.localizationService.strings.AppAdmin_ComponentSharepointNews,
      },
      {
        icon: 'Birthday',
        key: 'IntraActive-Birthday',
        text: this.localizationService.strings.AppAdmin_ComponentBirthday,
      }
    ];
    if (this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components.length < 2) {
      componentSelectorOptions.push({
        iconUrl: 'https://intraactivestorage.blob.core.windows.net/cdn/icons/Code/SVG/ic_fluent_code_24_regular.svg',
        key: 'EmbeddedPage',
        text: this.localizationService.strings.AppAdmin_ComponentEmbeddedPage,
      });
      componentSelectorOptions.push({
        icon: 'LinkExternal',
        key: 'ExternalLink',
        text: this.localizationService.strings.AppAdmin_ComponentExternalLink,
      });
      componentSelectorOptions.push({
        icon: 'Phone',
        key: 'IntraActive-Phonebook',
        text: this.localizationService.strings.AppAdmin_ComponentPhonebook,
      });
      componentSelectorOptions.push({
        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Organization/SVG/ic_fluent_organization_48_regular.svg",
        key: 'IntraActive-ContentOrganizerPortal',
        text: this.localizationService.strings.AppAdmin_ContentOrganizerPortal,
      });
      componentSelectorOptions.push({
        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Bot/SVG/ic_fluent_bot_24_regular.svg",
        key: 'IntraActive-AIAssistant',
        text: this.localizationService.strings.AppAdmin_AIAssistant,
      });
      if (this.props.appAdminStore.selectedPageIndex > 0) {
        componentSelectorOptions.push({
          icon: 'Map',
          key: 'IntraActive-ContentOrganizer',
          text: this.localizationService.strings.AppAdmin_ComponentContentMap,
        });
      }
      if (this.props.appAdminStore.selectedPageIndex > 0) {
        componentSelectorOptions.push({
          icon: 'Compass',
          key: 'IntraActive-NavigatorAdmin',
          text: this.localizationService.strings.AppAdmin_ComponentNavigator,
        });
      }
    }
    const displayOptions: IOptionSelectorItem[] = [
      {
        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/TextBox/SVG/ic_fluent_textbox_24_regular.svg",
        key: "Card view",
        text: this.localizationService.strings.AppAdmin_CardView
      },
      {
        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Apps%20List/SVG/ic_fluent_apps_list_24_regular.svg",
        key: "List view",
        text: this.localizationService.strings.AppAdmin_ListView
      }
    ]
    let component: IAppComponent = this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex];
    // if (component && !component?.instance?.settings && this.state.instances) {
    //   this.state.instances.forEach((instance: IInstance) => {
    //     if (instance.id === component.instance?.id) {
    //       component.instance = instance;
    //     }
    //   })
    // }
    if (component && (component.type === "IntraActive-Messages" || component.type === "Sharepoint-News")) {
      displayOptions.push({
        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Arrow%20Autofit%20Width/SVG/ic_fluent_arrow_autofit_width_24_regular.svg",
        key: "RotatorView",
        text: this.localizationService.strings.AppAdmin_RotatorView
      });
    }
    let items: ITreeViewItem[] = [];
    if (this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex] && component.type === "IntraActive-Birthday") {
      items = [
        {
          title: this.localizationService.strings.AppAdmin_BirthdayPropertyPhone,
          id: "phone",
          checked: this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex].birthdayProperties ? component.birthdayProperties.indexOf("phone") !== -1 : false,
          children: undefined
        },
        {
          title: this.localizationService.strings.AppAdmin_BirthdayPropertyJobTitle,
          id: "jobTitle",
          checked: this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex].birthdayProperties ? component.birthdayProperties.indexOf("jobTitle") !== -1 : false,
          children: undefined
        },
        {
          title: this.localizationService.strings.AppAdmin_BirthdayPropertyLocation,
          id: "location",
          checked: this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex].birthdayProperties ? component.birthdayProperties.indexOf("location") !== -1 : false,
          children: undefined
        },
        {
          title: this.localizationService.strings.AppAdmin_BirthdayPropertyDepartment,
          id: "department",
          checked: this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex].birthdayProperties ? component.birthdayProperties.indexOf("department") !== -1 : false,
          children: undefined
        },
        {
          title: this.localizationService.strings.AppAdmin_BirthdayPropertyCompany,
          id: "company",
          checked: this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex].birthdayProperties ? component.birthdayProperties.indexOf("company") !== -1 : false,
          children: undefined
        },
        {
          title: this.localizationService.strings.AppAdmin_BirthdayPropertyEmail,
          id: "email",
          checked: this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex].birthdayProperties ? component.birthdayProperties.indexOf("email") !== -1 : false,
          children: undefined
        },
        {
          title: this.localizationService.strings.AppAdmin_BirthdayPropertyChat,
          id: "chat",
          checked: this.props.appAdminStore.instance?.settings?.menu[this.props.appAdminStore.selectedPageIndex]?.components[this.props.appAdminStore.selectedComponentIndex].birthdayProperties ? component.birthdayProperties.indexOf("chat") !== -1 : false,
          children: undefined
        },
      ];
    }
    var anniversaryItems: ITreeViewItem[] = [];
    if (component && component.type === "IntraActive-Birthday") {
      if (component && !component.yearsAnniversary) {
        component.yearsAnniversary = {}
      }
      for (let i = 5; i <= 50; i += 5) {
        anniversaryItems.push({
          checked: component.yearsAnniversary[i.toString()] ? component.yearsAnniversary[i.toString()] : false,
          children: undefined,
          id: i,
          title: `${i} ${this.localizationService.strings.AppAdmin_ComponentYears}`
        });
      }
    }
    content = (
      <div
        className={styles.IA_editPanel}
        id="IA_ComponentEditPanel"
      >
        <IAGroup
          label={this.localizationService.strings.AppAdmin_MenuItemConfiguration}
        >
          <IAOptionSelector
            label={this.localizationService.strings.AppAdmin_SelectComponent}
            options={componentSelectorOptions}
            disabled={component && component.type != undefined}
            selected={component ? component.type : undefined}
            highlightColor={"#23879b"}
            onChange={async (type: any) => {
              component.type = type;
              if (type === "IntraActive-Messages" || component.type === "IntraActive-Events" || component.type === "Sharepoint-News") {
                if (type === "IntraActive-Messages" || component.type === "IntraActive-Events") {
                  await this.loadInstances();
                  const firstInstance = this.props.appAdminStore.availableInstancesForComponent?.length > 0 && this.props.appAdminStore.availableInstancesForComponent[0] && this.props.appAdminStore.availableInstancesForComponent[0].key ? JSON.parse(this.props.appAdminStore.availableInstancesForComponent[0].key) : undefined;
                  component.instance = component.instance ? component.instance : firstInstance;
                }
                component.display = "Card view";
                component.numberOfCards = 4;
                component.showDropShadow = true;
                component.showImage = true;
                component.showCardTitle = true;
                component.showTeaser = true;
                component.showContact = true;
                component.showStartDate = true;
                component.showLikes = true;
                component.showComments = true;
                component.showViews = true;
                component.showCategories = true;
                component.borderRadius = 10;
                component.autoRotate = true;
              }
              if (type === "IntraActive-Birthday") {
                component.birthdayType = "Birthday"
                component.display = "Card view";
                component.numberOfCards = 4;
                component.numberOfDays = 364;
              }
              if (type === "IntraActive-ContentOrganizerPortal") {
                await this.loadInstances();
              }
              this.props.appAdminStore.checkForChanges();
              if (this.props.hasChanged) {
                this.props.hasChanged();
              }
              setTimeout(() => {
                this.forceUpdate();
                this.props.hasChanged();
              }, 200);
            }}
            style={{ clear: "none", marginTop: 20 }}
          />
          {component && component.type === "IntraActive-ContentOrganizerPortal" &&
            <div style={{
              marginTop: 20,
              clear: "both",
              position: "relative",
              float: "left",
              width: "100%"
            }}>
              <IATreeview
                label={this.localizationService.strings.AppAdmin_SelectContentOrganizers}
                highlightColor={"#23879b"}
                expanded={true}
                items={this.props.appAdminStore.availableInstancesForComponent}
                actionType="Checkbox"
                structureType="Arrow"
                onChange={(instances) => {
                  if (instances && instances.length === 1) {
                    const contentOrganizers: string[] = [];
                    instances[0].children.forEach((instance: IInstance) => {
                      if (instance.checked || instances[0].checked) {
                        contentOrganizers.push(instance?.id);
                      }
                    });
                    component.contentOrganizers = contentOrganizers;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
              {this.props.appAdminStore.loadingInstances &&
                <div style={{
                  marginTop: 20,
                  clear: "both",
                  position: "relative",
                  float: "left",
                  color: "#999999"
                }}>
                  {new LocalizationService().strings.ContentCreatorAdmin_LoadingInstances}
                </div>
              }
              <div style={{
                marginTop: 20,
                clear: "both",
                position: "relative",
                float: "left",
                width: "100%"
              }}>
                <IATextField
                  label={this.localizationService.strings.AppAdmin_ContentOrganizerPortalSearchPlaceholder}
                  placeholder={this.localizationService.strings.AppAdmin_ContentOrganizerSearch}
                  text={component ? component.placeholderText : ""}
                  onChange={(placeholderText: string) => {
                    component.placeholderText = placeholderText;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                    this.forceUpdate();
                    this.props.hasChanged();
                  }}
                  style={{
                    width: "100%"
                  }}
                />
              </div>
            </div>
          }
          {component && component.type === "IntraActive-Birthday" &&
            <IAOptionSelector
              label={this.localizationService.strings.AppAdmin_SelectBirthdayType}
              options={[
                {
                  icon: "Birthday",
                  key: "Birthday",
                  text: this.localizationService.strings.AppAdmin_BirthdayOptionBirthday
                },
                {
                  icon: "Champagne",
                  key: "Anniversary",
                  text: this.localizationService.strings.AppAdmin_BirthdayOptionAnniversary
                }
              ]}
              selected={component.birthdayType}
              highlightColor={"#23879b"}
              onChange={(birthdayType: any) => {
                component.birthdayType = birthdayType;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
              style={{ clear: "none", marginTop: 20 }}
            />
          }
          {this.props.appAdminStore.loadingInstances && component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events") &&
            <div style={{
              marginTop: 20,
              clear: "both",
              position: "relative",
              float: "left",
              color: "#999999"
            }}>
              {new LocalizationService().strings.ContentCreatorAdmin_LoadingInstances}
            </div>
          }
          {component?.instance?.id && this.props.appAdminStore.availableInstancesForComponent && !this.props.appAdminStore.loadingInstances && component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events" || component.type === "IntraActive-NavigatorAdmin" || component.type === "IntraActive-ContentOrganizer") &&
            <div style={{
              marginTop: 0,
              clear: "both",
              position: "relative",
              float: "left",
              width: "100%"
            }}
            >
              <IADropDown
                label={`${new LocalizationService().strings.ContentCreatorAdmin_SelectInstance}*`}
                selectedOption={component ? component.instance.id : (this.props.appAdminStore.availableInstancesForComponent ? this.props.appAdminStore.availableInstancesForComponent[0].id : undefined)}
                options={this.props.appAdminStore.availableInstancesForComponent}
                style={{
                  width: "100%",
                  marginBottom: 20,
                  float: "left"
                }}
                onChange={(instanceId: string) => {
                  if (instanceId) {
                    this.state.instances.forEach((instance: IInstance) => {
                      if (instanceId === instance?.id) {
                        component.instance = instance;
                      }
                    })
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                    this.forceUpdate();
                    this.props.hasChanged();
                  }
                }}
              />
            </div>
          }
          {component && (component.type === "Sharepoint-News") &&
            <IATextField
              label={`${this.localizationService.strings.AppAdmin_SelectedSharepointSite}*`}
              text={component ? component.siteCollection : ""}
              onChange={(siteCollection: string) => {
                if (component.siteCollection !== siteCollection) {
                  clearTimeout(this.searchDelay);
                  component.siteCollection = siteCollection;
                  this.searchDelay = window.setTimeout(() => {
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                    this.forceUpdate();
                    this.props.hasChanged();
                  }, 1000);
                }
              }}
            />
          }
          {component && (component.type === "EmbeddedPage" || component.type === "ExternalLink") &&
            <IATextField
              label={this.localizationService.strings.AppAdmin_MenuItemUrl}
              text={component ? component.url : ""}
              onChange={(url: string) => {
                if (component.type === "EmbeddedPage" && url?.indexOf(".sharepoint.com") !== -1) {
                  component.url = "";
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  alert(this.localizationService.strings.AppAdmin_SharepointPagesCanNotBeEmbedded);
                } else {
                  component.url = url;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
            />
          }
          {component && component.type === "EmbeddedPage" && component.url !== undefined && component.url.indexOf("sharepoint.com") !== -1 &&
            <IAToggle
              style={{
                marginTop: 10
              }}
              label={this.localizationService.strings.AppAdmin_ShowNavigator}
              checked={component ? component.showNavigator : undefined}
              color="#23879b"
              onChange={() => {
                component.showNavigator = !component.showNavigator;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
            />
          }
          {component && (component.type === "EmbeddedPage" && component.url !== undefined && component.url.indexOf("sharepoint.com") !== -1 && component.showNavigator) &&
            <IAToggle
              style={{
                marginTop: 10
              }}
              label={this.localizationService.strings.AppAdmin_LoadLinksExternally}
              checked={component ? component.loadLinksExternally : undefined}
              color="#23879b"
              onChange={() => {
                component.loadLinksExternally = !component.loadLinksExternally;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
            />
          }
          {component && component.type === "IntraActive-Phonebook" &&
            <div>
              <IATextField
                label={this.localizationService.strings.AppAdmin_MenuItemSiteCollection}
                text={component ? component.siteCollection : ""}
                placeholder={this.localizationService.strings.AppAdmin_SiteCollectionPlaceholder}
                onChange={(siteCollection: string) => {
                  component.siteCollection = siteCollection;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
                onBlur={() => this.props.appAdminStore.reloadPage()}
              />
              <IATextField
                label={this.localizationService.strings.AppAdmin_MenuItemResultSource}
                text={component ? component.searchResultId : ""}
                placeholder={this.localizationService.strings.AppAdmin_ResultSourcePlaceholder}
                onChange={(searchResultId: string) => {
                  component.searchResultId = searchResultId;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
                onBlur={() => this.props.appAdminStore.reloadPage()}
                style={{ marginBottom: '20px' }}
              />
            </div>
          }
        </IAGroup>
        {component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events" || component.type === "IntraActive-Phonebook" || component.type === "IntraActive-Birthday" || component.type === "Sharepoint-News") &&
          <IAGroup
            label={this.localizationService.strings.AppAdmin_Layout}
          >
            <IAToggle
              style={{
                marginTop: 10
              }}
              label={this.localizationService.strings.AppAdmin_ShowTitle}
              checked={component && component.showTitle}
              color="#23879b"
              onChange={() => {
                component.showTitle = !component.showTitle;
                component.title = undefined;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
            />
            {component.showTitle &&
              <IATextField
                label={this.localizationService.strings.AppAdmin_ComponentTitle}
                text={component ? component.title : ""}
                onChange={(title: string) => {
                  component.title = title;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events" || component.type === "IntraActive-Birthday" || component.type === "Sharepoint-News") &&
              <IAOptionSelector
                label={this.localizationService.strings.AppAdmin_SelectDisplay}
                options={displayOptions}
                selected={component && component.display}
                highlightColor={"#23879b"}
                onChange={(display: any) => {
                  component.display = display;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
                style={{ clear: "none", marginTop: 20 }}
              />
            }
            {component && component.type === "IntraActive-Birthday" &&
              <IAIconPicker
                label={this.localizationService.strings.AppAdmin_BirthdayIcon}
                selectedIcon={component && component.birthdayIcon ? component.birthdayIcon : "Birthday"}
                color={"#23879b"}
                select={(birthdayIcon: string) => {
                  component.birthdayIcon = birthdayIcon;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
                shouldBeAbleToClear={false}
                clear={() => { }}
              />
            }
            {component && component.type === "IntraActive-Events" &&
              <IADropDown
                label={this.localizationService.strings.AppAdmin_SelectPeriod}
                selectedOption={component ? component.showPeriode : undefined}
                options={[
                  {
                    key: "upcomming",
                    value: this.localizationService.strings.AppAdmin_PeriodeUpcoming
                  },
                  {
                    key: "day",
                    value: this.localizationService.strings.AppAdmin_PeriodeDay
                  },
                  {
                    key: "week",
                    value: this.localizationService.strings.AppAdmin_PeriodeWeek
                  },
                  {
                    key: "month",
                    value: this.localizationService.strings.AppAdmin_PeriodeMonth
                  },
                  {
                    key: "fullView",
                    value: this.localizationService.strings.AppAdmin_PeriodeFullView
                  }
                ]}
                onChange={(periode) => {
                  if (periode !== component.showPeriode) {
                    component.showPeriode = periode;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                    this.forceUpdate()
                  }
                }}
                style={{
                  float: "left",
                  width: 374
                }}
              />
            }
            {component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events" || component.type === "IntraActive-Birthday" || component.type === "Sharepoint-News") &&
              <IANumberField
                label={this.localizationService.strings.AppAdmin_NumberOfMessages}
                number={component ? component.numberOfCards : undefined}
                onChange={(numberOfCards: number) => {
                  component.numberOfCards = numberOfCards;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
                styles={{
                  width: "100%"
                }}
              />
            }
            {component?.instance?.settings?.enablePinMessages &&
              <>
                <IAToggle
                  style={{
                    marginTop: 10
                  }}
                  label={this.localizationService.strings.Messages_Settings_EnablePinnedMessages}
                  checked={component.showPinnedMessages != undefined ? component.showPinnedMessages : false}
                  color="#23879b"
                  onChange={() => {
                    component.showPinnedMessages = component.showPinnedMessages != undefined ? !component.showPinnedMessages : true;
                    if (!component.showPinnedMessages) {
                      component.onlyShowPinnedMessages = false;
                    }
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                    this.forceUpdate();
                    this.props.hasChanged();
                  }}
                />
                <IAToggle
                  style={{
                    marginTop: 10
                  }}
                  label={this.localizationService.strings.Messages_Settings_ShowOnlyPinnedMessages}
                  checked={component.onlyShowPinnedMessages != undefined ? component.onlyShowPinnedMessages : false}
                  color="#23879b"
                  disabled={!component.showPinnedMessages}
                  onChange={() => {
                    component.onlyShowPinnedMessages = component.onlyShowPinnedMessages != undefined ? !component.onlyShowPinnedMessages : true;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                    this.forceUpdate();
                    this.props.hasChanged();
                  }}
                />
              </>
            }
            {component && ((component.type === "IntraActive-Messages" && component.display !== "RotatorView") || (component.type === "IntraActive-Events" && component.display !== "RotatorView") || component.type === "IntraActive-Birthday") &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowPaging}
                checked={component.showPaging != undefined ? component.showPaging : true}
                color="#23879b"
                onChange={() => {
                  component.showPaging = component.showPaging != undefined ? !component.showPaging : false;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component && component.type === "IntraActive-Birthday" &&
              <div style={{ clear: "both", marginTop: 10, float: "left" }}>
                <IATreeview
                  label={this.localizationService.strings.AppAdmin_BirthdayProperties}
                  highlightColor={"#23879b"}
                  items={items}
                  actionType={"Checkbox"}
                  structureType={"Arrow"}
                  onChange={(selectedroperties: ITreeViewItem[]) => {
                    const birthdayProperties = [];
                    selectedroperties.forEach((property: ITreeViewItem) => {
                      if (property.checked) {
                        birthdayProperties.push(property.id.toString());
                      }
                    });
                    component.birthdayProperties = birthdayProperties;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                    this.forceUpdate();
                    this.props.hasChanged();
                  }}
                />
              </div>
            }
            {component && component.type === "IntraActive-Birthday" && (
              <IANumberField
                label={this.localizationService.strings.AppAdmin_NumberOfDays}
                number={component.numberOfDays}
                min={this.min}
                max={this.max}
                onChange={(numberOfDays: number) => {
                  if (numberOfDays >= this.min && numberOfDays <= this.max) {
                    component.numberOfDays = numberOfDays;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                    this.forceUpdate();
                  } else {
                    this.setState({ displayTooManyDaysErrorMessage: true });
                  }
                }}
                styles={{
                  width: "100%",
                }}
              />
            )}
            {this.state.displayTooManyDaysErrorMessage && (
              <div style={{ color: "red", marginTop: 10, float: "left" }}>
                {this.localizationService.strings.AppAdmin_NumberOutOfBounds.replace("{number}", this.max.toString())}
              </div>
            )}
            {(component && component.type === "IntraActive-Birthday" && component.birthdayType == "Anniversary") &&
              <div style={{ clear: "both", marginTop: 10, float: "left" }}>
                <IATreeview
                  highlightColor="#23879b"
                  items={anniversaryItems}
                  label={this.localizationService.strings.AppAdmin_ComponentAnniversaryIntervals}
                  actionType={"Checkbox"}
                  structureType={"Arrow"}
                  onChange={(selectedroperties: ITreeViewItem[]) => {
                    selectedroperties.forEach((property: ITreeViewItem) => {
                      component.yearsAnniversary[property.id] = property.checked;
                    });
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                    this.forceUpdate();
                    this.props.hasChanged();
                  }}
                />
              </div>
            }
            {(component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events")) &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowAdd}
                checked={component && component.showAdd}
                color="#23879b"
                onChange={() => {
                  component.showAdd = !component.showAdd;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events") &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowFilter}
                checked={component && component.showFilter}
                color="#23879b"
                onChange={() => {
                  component.showFilter = !component.showFilter;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component && component.type === "IntraActive-Messages" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowSearch}
                checked={component.showSearch}
                color="#23879b"
                onChange={() => {
                  component.showSearch = !component.showSearch;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
          </IAGroup>
        }
        {component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events" || component.type === "Sharepoint-News") &&
          <IAGroup
            label={this.localizationService.strings.AppAdmin_Card} // this.localizationService.strings.AppAdmin_List
          >
            <IAColorPicker
              label={this.localizationService.strings.AppAdmin_BackgroundColor}
              color={component && component.backgroundColor ? component.backgroundColor : "#ffffff"}
              onChange={(backgroundColor: string) => {
                component.backgroundColor = backgroundColor;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
              showClearButton={false}
            />
            <IAColorPicker
              label={this.localizationService.strings.AppAdmin_BorderColor}
              color={component && component.borderColor ? component.borderColor : "#eeeeee"}
              defaultColor="transparent"
              onChange={(borderColor: string) => {
                component.borderColor = borderColor;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
              showClearButton={true}
            />
            <IAColorPicker
              label={this.localizationService.strings.AppAdmin_TextColor}
              color={component && component.textColor ? component.textColor : "#333333"}
              onChange={(textColor: string) => {
                component.textColor = textColor;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
              showClearButton={false}
            />
            <IAColorPicker
              label={this.localizationService.strings.Messages_Settings_HighlightColor}
              color={component && component.higlightColor ? component.higlightColor : "#333333"}
              onChange={(higlightColor: string) => {
                component.higlightColor = higlightColor;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
              showClearButton={false}
            />
            {component && component.type === "IntraActive-Events" && component.display === "List view" &&
              <IAColorPicker
                label={this.localizationService.strings.AppAdmin_DateBadgeBackgroundColor}
                color={component && component.dateBadgeBackgroundColor ? component.dateBadgeBackgroundColor : "#ffffff"}
                onChange={(dateBadgeBackgroundColor: string) => {
                  component.dateBadgeBackgroundColor = dateBadgeBackgroundColor;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
                showClearButton={true}
              />
            }
            {component.type === "IntraActive-Events" && component.display === "List view" &&
              <IAColorPicker
                label={this.localizationService.strings.AppAdmin_DateBadgeTextColor}
                color={component && component.dateBadgeTextColor ? component.dateBadgeTextColor : "#ffffff"}
                onChange={(dateBadgeTextColor: string) => {
                  component.dateBadgeTextColor = dateBadgeTextColor;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
                showClearButton={true}
              />
            }
            <IADropDown
              label={`${new LocalizationService().strings.AppAdmin_BorderRadius}`}
              selectedOption={component.borderRadius ? `${component.borderRadius}` : "0"}
              options={[
                {
                  "key": "0",
                  "value": new LocalizationService().strings.AppAdmin_BorderRadius0,
                },
                {
                  "key": "10",
                  "value": new LocalizationService().strings.AppAdmin_BorderRadius1,
                },
                {
                  "key": "15",
                  "value": new LocalizationService().strings.AppAdmin_BorderRadius2,
                },
                {
                  "key": "20",
                  "value": new LocalizationService().strings.AppAdmin_BorderRadius3,
                }
              ]}
              style={{
                width: "100%",
                marginTop: 20,
                marginBottom: 10,
                float: "left"
              }}
              onChange={(borderRadius: string) => {
                component.borderRadius = parseInt(borderRadius);
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
            />
            <IAToggle
              style={{
                marginTop: 10
              }}
              label={this.localizationService.strings.AppAdmin_ShowShadow}
              checked={component && component.showDropShadow}
              color="#23879b"
              onChange={() => {
                component.showDropShadow = !component.showDropShadow;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
            />
            {component && component.display !== "RotatorView" && component.type !== "Sharepoint-News" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowImage}
                checked={component && component.showImage}
                color="#23879b"
                onChange={() => {
                  component.showImage = !component.showImage;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component.type !== "Sharepoint-News" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowTitle}
                checked={component && component.showCardTitle}
                color="#23879b"
                onChange={() => {
                  component.showCardTitle = component.showCardTitle ? !component.showCardTitle : true;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component && component.type === "IntraActive-Messages" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowTeaser}
                checked={component && component.showTeaser}
                color="#23879b"
                onChange={() => {
                  component.showTeaser = !component.showTeaser;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component.type !== "Sharepoint-News" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowContact}
                checked={component && component.showContact}
                color="#23879b"
                onChange={() => {
                  component.showContact = !component.showContact;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            <IAToggle
              style={{
                marginTop: 10
              }}
              label={this.localizationService.strings.AppAdmin_ShowStartdate}
              checked={component && component.showStartDate}
              color="#23879b"
              onChange={() => {
                component.showStartDate = !component.showStartDate;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
            />
            {component && component.type === "IntraActive-Events" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowLocation}
                checked={component && component.showLocation}
                color="#23879b"
                onChange={() => {
                  component.showLocation = !component.showLocation;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component.type !== "Sharepoint-News" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowLikes}
                checked={component && component.showLikes}
                color="#23879b"
                onChange={() => {
                  component.showLikes = !component.showLikes;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component.type !== "Sharepoint-News" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowComments}
                checked={component && component.showComments}
                color="#23879b"
                onChange={() => {
                  component.showComments = !component.showComments;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component && component.type === "IntraActive-Messages" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowViews}
                checked={component && component.showViews}
                color="#23879b"
                onChange={() => {
                  component.showViews = !component.showViews;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component && component.type === "IntraActive-Messages" && component.display !== "RotatorView" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowFunctionButton}
                checked={component && component.showFunctionButton}
                color="#23879b"
                onChange={() => {
                  component.showFunctionButton = !component.showFunctionButton;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component.type !== "Sharepoint-News" &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_ShowCategories}
                checked={component && component.showCategories}
                color="#23879b"
                onChange={() => {
                  component.showCategories = !component.showCategories;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events") &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.AppAdmin_OnlyShowIfThereIsContent}
                checked={component && component.onlyShowIfThereIsContent}
                color="#23879b"
                onChange={() => {
                  component.onlyShowIfThereIsContent = !component.onlyShowIfThereIsContent;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
            {component && !component.onlyShowIfThereIsContent && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events") &&
              <IATextField
                label={this.localizationService.strings.Messages_Settings_TextIfNoContent}
                text={component.noContentText ?? this.localizationService.strings.Messages_NoContentToShow}
                toolTipText={this.localizationService.strings.Messages_Settings_TextIfNoContentTooltip + "'" + this.localizationService.strings.Messages_NoContentToShow + "'"}
                onChange={(noContentText: string) => {
                  component.noContentText = noContentText;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }

            {component && (component.type === "IntraActive-Messages" && component.display == "RotatorView") &&
              <IAToggle
                style={{
                  marginTop: 10
                }}
                label={this.localizationService.strings.Messages_Settings_AutoRotate}
                checked={component && component.autoRotate}
                color="#23879b"
                onChange={() => {
                  component.autoRotate = !component.autoRotate;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
            }
          </IAGroup>
        }

        {component && (component.type === "IntraActive-Messages" || component.type === "IntraActive-Events") &&
          <IAGroup
            label={this.localizationService.strings.AppAdmin_Filterring}
          >
            {component.instance &&
              <div style={{
                marginTop: 20,
                float: "left",
                clear: "both"
              }}>
                <IAToggle
                  label={this.localizationService.strings.AppAdmin_FilterByCategory}
                  checked={component.filterByCategory}
                  color="#23879b"
                  onChange={() => {
                    component.filterByCategory = !component.filterByCategory;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                    this.forceUpdate();
                    this.props.hasChanged();
                  }}
                />
                {component.filterByCategory &&
                  <IATermCheckbox
                    environment={this.props.appAdminStore.environment}
                    token={this.props.appAdminStore.token}
                    tenant={this.props.appAdminStore.tenant}
                    instance={component.instance}
                    component={this.getComponentFromType()}
                    highlightColor={"#23879b"}
                    selectedValues={component.categories ? component.categories : undefined}
                    onChange={(selectedValues) => {
                      component.categories = selectedValues;
                      this.props.appAdminStore.checkForChanges();
                      if (this.props.hasChanged) {
                        this.props.hasChanged();
                      }
                      this.forceUpdate();
                      this.props.hasChanged();
                    }}
                  />
                }
              </div>
            }
            <IAToggle
              style={{
                marginTop: 10
              }}
              label={this.localizationService.strings.AppAdmin_ActivateTargeting}
              checked={component.useTargeting}
              color="#23879b"
              onChange={() => {
                component.useTargeting = !component.useTargeting;
                this.props.appAdminStore.checkForChanges();
                if (this.props.hasChanged) {
                  this.props.hasChanged();
                }
                this.forceUpdate();
                this.props.hasChanged();
              }}
            />
          </IAGroup>
        }
        {/* AI assistant */}
        {component && component.type === "IntraActive-AIAssistant" &&
          <>
            <IAGroup
              label={this.localizationService.strings.AppAdmin_Settings}
            >
              <IATextField
                label={this.localizationService.strings.Navigator_Content_AiTitle}
                placeholder={this.localizationService.strings.Navigator_Content_AiTitleFallback}
                text={component.title}
                borderRadius={5}
                onChange={(title: string) => {
                  component.title = title;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
              <IATextField
                label={this.localizationService.strings.Navigator_Content_AiExample}
                placeholder={this.localizationService.strings.Navigator_Content_AiExampleFallback}
                text={component.placeholderText}
                borderRadius={5}
                onChange={(placeholderText: string) => {
                  component.placeholderText = placeholderText;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
              <IATextField
                label={this.localizationService.strings.Navigator_Content_AiDescriptionHeadline}
                text={component.descriptionHeadline}
                borderRadius={5}
                onChange={(descriptionHeadline: string) => {
                  component.descriptionHeadline = descriptionHeadline;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
              <IATextField
                label={this.localizationService.strings.Navigator_Content_AiDescription}
                text={component.descriptionText}
                borderRadius={5}
                onChange={(descriptionText: string) => {
                  component.descriptionText = descriptionText;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
              />
              <IAIconPicker
                label={this.localizationService.strings.Navigator_Content_AiDescriptionIcon ?? "Description icon (optional)"}
                selectedIcon={component.descriptionIcon}
                color={"#23879b"}
                select={(descriptionIcon: string) => {
                  component.descriptionIcon = descriptionIcon;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
                shouldBeAbleToClear={false}
                clear={() => { }}
              />
              <IADropDown
                label={this.localizationService.strings.Navigator_Content_AiContentLanguage}
                selectedOption={component.aiContentLanguage ?? undefined}
                options={[
                  {
                    key: "en",
                    value: this.localizationService.strings.Navigator_Admin_ContentOnlyInEnglish,
                  },
                  ...[{
                    key: "da",
                    value: this.localizationService.strings.Panel_NavigatorBar_LanguageDanish,
                  },
                  {
                    key: "de",
                    value: this.localizationService.strings.Panel_NavigatorBar_LanguageGerman,
                  },
                  {
                    key: "nb",
                    value: this.localizationService.strings.Panel_NavigatorBar_LanguageNorwegian,
                  },
                  {
                    key: "sv",
                    value: this.localizationService.strings.Panel_NavigatorBar_LanguageSwedish,
                  },
                  {
                    key: "fi",
                    value: new LocalizationService().strings.Panel_NavigatorBar_LanguageFinnish,
                  },
                  {
                    key: "nl",
                    value: this.localizationService.strings.Panel_NavigatorBar_LanguageDutch,
                  }].sort((a, b) => a.value.localeCompare(b.value))
                ]}
                borderRadius={5}
                onChange={(language: string) => {
                  component.aiContentLanguage = language;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                  this.forceUpdate();
                  this.props.hasChanged();
                }}
                style={{
                  float: "left"
                }}
              />
            </IAGroup>
            <IAGroup
              label={this.localizationService.strings.AppAdmin_SelectedSites}
            >
              <div className={styles.IA_aiSitesWrapper}>
                {component.aiSites?.length > 0
                  ?
                  component.aiSites.map((site, index) => {
                    return (
                      <div
                        key={`selectedSite_${site.siteCollection}`}
                        onClick={() => {
                          component.aiSites.splice(index, 1);
                          this.props.appAdminStore.checkForChanges();
                          if (this.props.hasChanged) {
                            this.props.hasChanged();
                          }
                          this.forceUpdate();
                          this.props.hasChanged();
                        }}
                        className={styles.IA_aiSite}
                        style={{
                          backgroundColor: "#23879b",
                          color: "white"
                        }}
                      >
                        <b>{site.title}</b><br />
                        {site.siteCollection}
                      </div>
                    )
                  })
                  :
                  <div>No selected sites</div>
                }
              </div>
              <IATextField
                placeholder="Search all SharePoint sites"
                onChange={(searchText) => {
                  this.setState({ searchText });
                  clearTimeout(this.queryDelay);
                  this.queryDelay = setTimeout(() => {
                    if (this.state.searchText) {
                      this.searchSharepointSites();
                    } else {
                      this.setState({ sharePointSites: undefined });
                    }
                  }, 500);
                }}
                disabled={this.state.isLoadingContentOrganizers}
                text={this.state.searchText}
                borderRadius={5}
                style={{
                  marginTop: 0,
                  marginBottom: 10
                }}
              />
              {this.state.isLoadingContentOrganizers || this.state.isLoadingSharepointSitesQuery
                ?
                <>
                  <div className={styles.IA_aiSitesWrapper}>
                    <IASkeleton
                      skeleton={{ height: 54, width: 144, borderRadius: 5, marginTop: 5, marginRight: 5 }}
                    />
                    <IASkeleton
                      skeleton={{ height: 54, width: 105, borderRadius: 5, marginTop: 5, marginRight: 5 }}
                    />
                    <IASkeleton
                      skeleton={{ height: 54, width: 85, borderRadius: 5, marginTop: 5, marginRight: 5 }}
                    />
                    <IASkeleton
                      skeleton={{ height: 54, width: 124, borderRadius: 5, marginTop: 5, marginRight: 5 }}
                    />
                    <IASkeleton
                      skeleton={{ height: 54, width: 85, borderRadius: 5, marginTop: 5, marginRight: 5 }}
                    />
                  </div>
                </>
                :
                <>
                  <div className={styles.IA_aiSitesWrapper}>
                    {this.state.searchText && this.state.sharePointSites
                      ?
                      <>{this.renderSites(this.state.sharePointSites)}</>
                      :
                      <>{this.renderSites(this.state.contentOrganizerInstances)}</>
                    }
                  </div>
                </>
              }
            </IAGroup>
          </>
        }
      </div>
    );
    return (
      <IAPanel
        showNavigationBar={true}
        component={"AppAdmin"}
        open={this.props.appAdminStore.editComponent}
        transition="slide"
        width={600}
        isMobile={false}
        highlightColor={"#23879b"}
        marginTop={location.origin.indexOf("sharepoint.com") !== -1 ? 159 : 50}
        overlayColor={"transparent"}
        disableSaving={this.props.appAdminStore.isSaving}
        close={() => {
          this.close();
        }}
        onDelete={() => {
          this.props.appAdminStore.componentIndexToDelete = this.props.appAdminStore.selectedComponentIndex;
        }}
      >
        {content}
      </IAPanel>
    );
  }

  private close(): void {
    this.props.appAdminStore.editComponent = false;
    this.setState({ instancesForComponent: undefined });
    setTimeout(() => {
      this.props.appAdminStore.selectedComponentIndex = undefined;
    }, 500);
  }


}